import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSessionChecks,
  getSessionCheckStats,
  getSessionCheckFacets,
  getSessionModel,
  trainSessionModel,
  activateSessionModel,
  deactivateSessionModel,
  type ModelSummary,
  type SessionCheck,
  type SessionLabel,
  type SessionVerdict,
  type VerdictCounts,
} from '@/api/device-session-checks';
import { MultiSelect } from '@/components/MultiSelect';
import { Select } from '@/components/Select';
import { formatDateTime } from '@/lib/datetime';
import { useAuthStore } from '@/store/auth';

const reasonLabels: Record<string, string> = {
  none: 'Not blocked',
  suspicious_apps: 'Suspicious Apps',
  no_sim: 'No SIM',
  geo_mismatch: 'GEO Mismatch',
  review_visit: 'Review / Emulator',
  motion_frozen: 'Frozen sensor',
  tz_mismatch: 'Timezone ≠ IP',
  battery_full: 'Battery 100% charging',
  device_burst: 'Same-model burst',
  bot_fleet: 'Scanner fleet build',
  behavior_bot: 'Bot by behavior',
};

// Сработавшие правила (lib/session-score.ts на воркере).
const signalLabels: Record<string, string> = {
  same_point: 'same pixel',
  metronome: 'metronome pauses',
  no_jolt: 'no jolt from taps',
  motion_zero: 'phone never moved',
  no_accel: 'no accelerometer',
  press_same: 'same press length',
  touch_flat: 'flat pressure/size',
  straight_swipes: 'straight swipes',
  jolt: 'jolts from taps',
  uneven_pauses: 'uneven pauses',
  spread_taps: 'spread taps',
  uneven_presses: 'uneven presses',
  fitts: 'farther = slower',
  moving: 'phone moves',
};
const BOT_SIGNALS = new Set(['same_point', 'metronome', 'no_jolt', 'motion_zero', 'no_accel', 'press_same', 'touch_flat', 'straight_swipes']);

const labelStyles: Record<Exclude<SessionLabel, null>, { label: string; className: string; title: string }> = {
  bot: { label: 'Bot', className: 'text-danger', title: 'Blocked as scanner fleet / same-model burst' },
  human: { label: 'Human', className: 'text-success', title: 'Registered or deposited from this device' },
  conflict: { label: 'Both', className: 'text-warning', title: 'Has both labels — left out of training' },
};

const verdictLabels: Record<SessionVerdict, string> = { human: 'Human', bot: 'Bot', unsure: 'Unsure' };

const verdictColors: Record<SessionVerdict, string> = {
  human: 'bg-success-soft text-success border-success/30',
  bot: 'bg-danger-soft text-danger border-danger/30',
  unsure: 'bg-surface-muted text-content-muted border-border',
};

const statusColors: Record<string, string> = {
  active: 'bg-success-soft text-success border-success/30',
  blocked: 'bg-danger-soft text-danger border-danger/30',
};

const SCOPE_OPTIONS = [
  { value: 'blocked', label: 'Blocked when checked' },
  { value: 'clean', label: 'Not blocked (sampled)' },
  { value: 'all', label: 'All results' },
];

const LABEL_OPTIONS = [
  { value: 'bot', label: 'Label: bot' },
  { value: 'human', label: 'Label: human' },
  { value: 'none', label: 'No label' },
];

const TEST_OPTIONS = [
  { value: 'exclude', label: 'QA: real only' },
  { value: 'all', label: 'QA: any' },
  { value: 'only', label: 'QA: test only' },
];

const VERDICT_OPTIONS = (['human', 'bot', 'unsure'] as SessionVerdict[]).map((v) => ({ value: v, label: verdictLabels[v] }));
const REASON_OPTIONS = Object.entries(reasonLabels).map(([value, label]) => ({ value, label }));

function pct(value: number, total: number): string | null {
  if (!total) return null;
  const p = (value / total) * 100;
  return p >= 10 ? `${p.toFixed(0)}%` : `${p.toFixed(1)}%`;
}

function StatCard({ label, value, color, total }: { label: string; value: number; color: string; total?: number }) {
  const pctText = total ? pct(value, total) : null;
  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-5">
      <p className="text-xs font-medium text-content-subtle uppercase tracking-wider">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <p className={`text-2xl font-bold ${color}`}>{value.toLocaleString()}</p>
        {pctText && <span className="text-xs font-medium text-content-subtle">{pctText}</span>}
      </div>
    </div>
  );
}

export function HumanCheckTab() {
  const [scope, setScope] = useState<'blocked' | 'clean' | 'all'>('blocked');
  const [verdictFilter, setVerdictFilter] = useState<string[]>([]);
  const [labelFilter, setLabelFilter] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [reasonFilter, setReasonFilter] = useState<string[]>([]);
  const [bundleFilter, setBundleFilter] = useState<string[]>([]);
  const [test, setTest] = useState<'exclude' | 'only' | 'all'>('exclude');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  const filterParams = useMemo(() => ({
    scope,
    verdict: verdictFilter,
    label: labelFilter,
    reason: reasonFilter,
    bundle: bundleFilter,
    test,
    search,
    dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
    dateTo: dateTo ? new Date(dateTo).toISOString() : undefined,
  }), [scope, verdictFilter, labelFilter, reasonFilter, bundleFilter, test, search, dateFrom, dateTo]);

  // Сводка по причинам не сужается фильтром вердикта и причины — иначе
  // таблица «какие правила режут людей» схлопнется до одной строки.
  const statsParams = useMemo(() => ({ ...filterParams, verdict: [], reason: [] }), [filterParams]);

  const { data: stats } = useQuery({
    queryKey: ['session-check-stats', statsParams],
    queryFn: () => getSessionCheckStats(statsParams),
    refetchInterval: 30000,
  });

  const { data: facets } = useQuery({
    queryKey: ['session-check-facets'],
    queryFn: getSessionCheckFacets,
    refetchInterval: 60000,
  });
  const bundleOptions = useMemo(
    () => (facets?.bundles || []).map((b) => ({ value: b.value, label: `${b.value} · ${b.count}` })),
    [facets]
  );

  const queryParams = useMemo(() => ({ ...filterParams, sortBy, sortDir, page, limit: 50 }), [filterParams, sortBy, sortDir, page]);
  const { data, isLoading } = useQuery({
    queryKey: ['session-checks', queryParams],
    queryFn: () => getSessionChecks(queryParams),
    refetchInterval: 15000,
  });

  const rows = data?.checks || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / 50);

  const resetPage = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1); };

  function toggleSort(col: string) {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
    setPage(1);
  }

  const reasonRows = useMemo(
    () => Object.entries(stats?.by_reason || {}).sort((a, b) => b[1].total - a[1].total),
    [stats]
  );

  const COLUMNS: { key: string; label: string; sortKey?: string }[] = [
    { key: 'created_at', label: 'Checked', sortKey: 'created_at' },
    { key: 'device_id', label: 'Device ID' },
    { key: 'bundle_id', label: 'Bundle ID', sortKey: 'bundle_id' },
    { key: 'device_model', label: 'Model / OS', sortKey: 'device_model' },
    { key: 'block_reason', label: 'Blocked by', sortKey: 'block_reason' },
    { key: 'bot_probability', label: 'Bot %', sortKey: 'bot_probability' },
    { key: 'confidence', label: 'Confidence', sortKey: 'confidence' },
    { key: 'verdict', label: 'Verdict' },
    { key: 'label', label: 'Label' },
    { key: 'current', label: 'Now' },
  ];

  const toggleRow = (id: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const t = stats?.thresholds;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border shadow-sm p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search by device ID, bundle ID or model..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          <Select value={scope} onChange={resetPage((v: string) => setScope(v as 'blocked' | 'clean' | 'all'))} options={SCOPE_OPTIONS} placeholder="Scope" />
          <MultiSelect value={verdictFilter} onChange={resetPage(setVerdictFilter)} options={VERDICT_OPTIONS} placeholder="Verdict" allLabel="Any verdict" />
          <MultiSelect value={labelFilter} onChange={resetPage(setLabelFilter)} options={LABEL_OPTIONS} placeholder="Label" allLabel="Any label" />
          <MultiSelect value={reasonFilter} onChange={resetPage(setReasonFilter)} options={REASON_OPTIONS} placeholder="Blocked by" allLabel="Any reason" />
          <MultiSelect
            value={bundleFilter}
            onChange={resetPage(setBundleFilter)}
            options={bundleOptions}
            placeholder="Bundle ID"
            allLabel="All Bundles"
            searchable
            className="min-w-[280px]"
          />
          <Select value={test} onChange={resetPage((v: string) => setTest(v as 'exclude' | 'only' | 'all'))} options={TEST_OPTIONS} placeholder="QA" />
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border">
          <span className="text-xs font-medium text-content-subtle uppercase tracking-wider">Checked</span>
          <input
            type="datetime-local"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="px-2.5 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
          <span className="text-content-subtle">—</span>
          <input
            type="datetime-local"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="px-2.5 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Checked devices" value={stats.total} color="text-content" />
            <StatCard label="Human" value={stats.human} color="text-success" total={stats.total} />
            <StatCard label="Bot" value={stats.bot} color="text-danger" total={stats.total} />
            <StatCard label="Unsure" value={stats.unsure} color="text-content-muted" total={stats.total} />
          </div>
          {t && (
            <p className="text-xs text-content-subtle">
              Human: bot % below {t.human_max} with data {t.human_min_confidence}%+. Bot: bot % {t.bot_min}+ with data{' '}
              {t.bot_min_confidence}%+. Too little data means «we don't know», even at bot % 0. One row per device, its
              latest result. Scored by {stats.model ? `model ${stats.model.id}` : 'rules'}. Letting humans through:{' '}
              <OnOff on={stats.human_pass} />, blocking bots by behavior: <OnOff on={stats.behavior_gate} />, asking
              sure bots: {stats.sample_sure_bot}%, asking clean devices: {stats.sample_clean}% (Settings → antifraud).
            </p>
          )}

          <ModelPanel />

          {reasonRows.length > 0 && (
            <div className="bg-surface rounded-xl border border-border shadow-sm overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-muted/50">
                  <tr>
                    {['Blocked by', 'Checked', 'Human', 'Bot', 'Unsure', 'Human share'].map((h) => (
                      <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-left text-content-subtle">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {reasonRows.map(([reason, c]: [string, VerdictCounts]) => (
                    <tr
                      key={reason}
                      className="cursor-pointer hover:bg-surface-muted/50"
                      onClick={() => { setReasonFilter([reason]); setPage(1); }}
                      title="Show these devices"
                    >
                      <td className="px-4 py-2 text-sm text-content">{reasonLabels[reason] || reason}</td>
                      <td className="px-4 py-2 text-sm text-content-muted">{c.total}</td>
                      <td className="px-4 py-2 text-sm text-success">{c.human}</td>
                      <td className="px-4 py-2 text-sm text-danger">{c.bot}</td>
                      <td className="px-4 py-2 text-sm text-content-muted">{c.unsure}</td>
                      <td className="px-4 py-2 text-sm font-medium text-content">{pct(c.human, c.total) ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Table */}
      <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-content-subtle">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-content-subtle">
            Пока ни одного результата — SDK с проверкой «человек или бот» ещё не доехал до устройств.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-muted/50">
                  <tr>
                    {COLUMNS.map((col) => {
                      const isActive = col.sortKey && sortBy === col.sortKey;
                      return (
                        <th
                          key={col.key}
                          onClick={() => col.sortKey && toggleSort(col.sortKey)}
                          className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-left select-none ${
                            col.sortKey ? 'cursor-pointer hover:text-primary' : ''
                          } ${isActive ? 'text-primary' : 'text-content-subtle'}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {col.label}
                            {col.sortKey && (
                              <span className="text-[10px]">{isActive ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r: SessionCheck) => (
                    <Fragment key={r.id}>
                    <tr className="hover:bg-surface-muted/50 cursor-pointer" onClick={() => toggleRow(r.id)} title="Show the numbers">
                      <td className="px-4 py-3 text-xs text-content-subtle whitespace-nowrap">
                        {formatDateTime(r.created_at)}
                        {r.results > 1 && <span className="block">{r.results} results</span>}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-content-muted max-w-[120px] truncate" title={r.device_id}>
                        {r.device_id.substring(0, 12)}...
                      </td>
                      <td className="px-4 py-3 text-sm text-content-muted whitespace-nowrap">
                        {r.bundle_id}
                        {r.is_test === 1 && (
                          <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium border bg-surface-muted text-content-subtle border-border">
                            QA
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-content-muted">
                        {r.device_model || '—'}
                        <span className="block text-xs text-content-subtle">{r.os_version || ''}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-content-muted">
                        {r.sdk_status === 'blocked' ? (reasonLabels[r.block_reason || 'none'] || r.block_reason) : 'Not blocked'}
                        {r.ip_country && <span className="block text-xs text-content-subtle">IP {r.ip_country}</span>}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-content">
                        {r.bot_probability}%
                        <span className="block text-[10px] font-normal text-content-subtle">{scoredByLabel(r.scored_by)}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-content-muted">{r.confidence}%</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${verdictColors[r.verdict]}`}>
                          {verdictLabels[r.verdict]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {r.label ? (
                          <span className={`font-medium ${labelStyles[r.label].className}`} title={labelStyles[r.label].title}>
                            {labelStyles[r.label].label}
                          </span>
                        ) : (
                          <span className="text-xs text-content-subtle">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {r.current_status == null ? (
                          <span className="text-xs text-content-subtle">—</span>
                        ) : (
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[r.current_status] || 'bg-surface-muted text-content-muted'}`}
                            title={r.current_reason && r.current_reason !== 'none' ? reasonLabels[r.current_reason] || r.current_reason : undefined}
                          >
                            {r.current_status}
                          </span>
                        )}
                      </td>
                    </tr>
                    {expanded.has(r.id) && (
                      <tr>
                        <td colSpan={COLUMNS.length} className="px-4 py-4 bg-surface-muted/80">
                          <CheckDetails r={r} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <p className="text-sm text-content-subtle">{total} devices total</p>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm rounded border border-border disabled:opacity-40 hover:bg-surface-muted"
                  >
                    Prev
                  </button>
                  <span className="px-3 py-1 text-sm text-content-subtle">{page} / {totalPages}</span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 text-sm rounded border border-border disabled:opacity-40 hover:bg-surface-muted"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function OnOff({ on }: { on: boolean }) {
  return <span className={on ? 'text-success font-medium' : 'font-medium'}>{on ? 'on' : 'off'}</span>;
}

function scoredByLabel(by: string | null): string {
  if (!by) return '';
  if (by === 'sdk') return 'phone';
  if (by.startsWith('model:')) return 'model';
  if (by.startsWith('unscored')) return `format ${by.replace('unscored_', '')}`;
  return by;
}

/** Цифры из /device/sessioninfo и сработавшие правила. */
function CheckDetails({ r }: { r: SessionCheck }) {
  let stats: Record<string, unknown> | null = null;
  try { stats = r.stats ? JSON.parse(r.stats) : null; } catch { stats = null; }
  const signals = (r.signals || '').split(',').filter(Boolean);
  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-content-subtle">
        <span>Device: <span className="font-mono text-content-muted">{r.device_id}</span></span>
        <span>Asked by: {r.asked_by || '—'}</span>
        <span>Scored by: {r.scored_by || '—'}</span>
      </div>
      {signals.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {signals.map((sg) => (
            <span
              key={sg}
              className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${
                BOT_SIGNALS.has(sg) ? 'bg-danger-soft text-danger border-danger/30' : 'bg-success-soft text-success border-success/30'
              }`}
            >
              {signalLabels[sg] || sg}
            </span>
          ))}
        </div>
      )}
      {stats ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1 font-mono text-xs">
          {Object.entries(stats).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2">
              <span className="text-content-subtle">{k}</span>
              <span className="text-content">{String(v)}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-content-subtle">No stats: an older SDK sent its own score.</p>
      )}
    </div>
  );
}

const pctText = (v: number) => `${(v * 100).toFixed(v < 0.1 ? 1 : 0)}%`;

function ModelMetricsView({ m }: { m: ModelSummary }) {
  const x = m.metrics;
  if (!x) return null;
  const a = x.at_thresholds;
  const topSplits = Object.entries(x.splits).sort((p, q) => q[1] - p[1]).slice(0, 6);
  return (
    <div className="text-xs text-content-muted space-y-1">
      <p>
        Trained {formatDateTime(m.trained_at)} on {x.bots} bots and {x.humans} humans, {m.trees} trees. Checked by{' '}
        {x.folds}-fold cross-validation (each device scored by a model that never saw it): AUC{' '}
        <span className="font-medium text-content">{x.auc}</span>.
      </p>
      <p>
        At bot ≥ {a.bot_min}% / human &lt; {a.human_max}%:{' '}
        <span className={a.bots_as_human > 0.01 ? 'text-danger font-medium' : 'text-success font-medium'}>
          bots passed as human {pctText(a.bots_as_human)}
        </span>
        , humans recognized {pctText(a.humans_as_human)}, humans called bot {pctText(a.humans_as_bot)}, bots caught{' '}
        {pctText(a.bots_as_bot)}.
      </p>
      {topSplits.length > 0 && (
        <p className="text-content-subtle">Leans on: {topSplits.map(([f, c]) => `${f} (${c})`).join(', ')}</p>
      )}
    </div>
  );
}

/**
 * Модель «человек или бот»: сколько размеченных устройств, обучение
 * кандидата, включение. Пока модель не включена, оценивают правила.
 */
function ModelPanel() {
  const queryClient = useQueryClient();
  const isSuper = useAuthStore((s) => s.admin?.role === 'super_admin');
  const { data } = useQuery({ queryKey: ['session-model'], queryFn: getSessionModel, refetchInterval: 60000 });
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    setError(null);
    queryClient.invalidateQueries({ queryKey: ['session-model'] });
    queryClient.invalidateQueries({ queryKey: ['session-check-stats'] });
    queryClient.invalidateQueries({ queryKey: ['session-checks'] });
  };
  const onError = (e: unknown) => setError(e instanceof Error ? e.message : String(e));
  const train = useMutation({ mutationFn: trainSessionModel, onSuccess: refresh, onError });
  const activate = useMutation({ mutationFn: activateSessionModel, onSuccess: refresh, onError });
  const deactivate = useMutation({ mutationFn: deactivateSessionModel, onSuccess: refresh, onError });
  if (!data) return null;

  const enough = data.labels.bot >= data.min_per_class && data.labels.human >= data.min_per_class;
  const candidateIsActive = data.candidate && data.active && data.candidate.id === data.active.id;
  const busy = train.isPending || activate.isPending || deactivate.isPending;

  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-content">Model</p>
          <p className="text-xs text-content-subtle mt-0.5">
            Labeled devices with stats: <span className="text-danger font-medium">{data.labels.bot} bots</span> (blocked
            as scanner fleet or same-model burst), <span className="text-success font-medium">{data.labels.human} humans</span>{' '}
            (registered or deposited), {data.labels.none} unlabeled. Training needs {data.min_per_class} of each.
          </p>
        </div>
        {isSuper && (
          <div className="flex gap-2">
            <button
              onClick={() => train.mutate()}
              disabled={busy || !enough}
              title={enough ? 'Train a new candidate model' : 'Not enough labeled devices yet'}
              className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-surface-muted disabled:opacity-40"
            >
              {train.isPending ? 'Training…' : 'Train'}
            </button>
            {data.active && (
              <button
                onClick={() => deactivate.mutate()}
                disabled={busy}
                className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-surface-muted disabled:opacity-40"
              >
                Back to rules
              </button>
            )}
          </div>
        )}
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-1">
          <p className="text-xs font-medium text-content-subtle uppercase tracking-wider">In use</p>
          {data.active ? (
            <>
              <p className="text-sm text-content">Model {data.active.id}</p>
              <ModelMetricsView m={data.active} />
            </>
          ) : (
            <p className="text-sm text-content-muted">Rules (same pixel, metronome pauses, no jolt from taps…)</p>
          )}
        </div>
        {data.candidate && !candidateIsActive && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-content-subtle uppercase tracking-wider">Trained, not in use</p>
            <p className="text-sm text-content">Model {data.candidate.id}</p>
            <ModelMetricsView m={data.candidate} />
            {isSuper && (
              <button
                onClick={() => activate.mutate()}
                disabled={busy}
                className="mt-2 px-3 py-1.5 text-sm rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-40"
              >
                {activate.isPending ? 'Switching…' : 'Use this model'}
              </button>
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-content-subtle">
        Switching re-scores every stored result; workers pick the new model up within 5 minutes.
      </p>
    </div>
  );
}
